import { ChevronDown, CircleUser, LogOut, Menu, MessageCircle, Settings, X, type LucideIcon } from 'lucide-react'
import { Suspense, useEffect, useRef, useState, type ComponentType } from 'react'
import { Link, NavLink, Outlet, ScrollRestoration, useLocation, useNavigation } from 'react-router'
import { useContactEmail, useMe } from '../data/queries'
import { api } from '../lib/api'
import { cn, firstName } from '../lib/utils'
import { canAccessPath, canUse } from '../modules/tenants'
import { RaisNavLabel } from '../modules/rais/RaisNavLabel'
import { RaisDrawerProvider, useRaisDrawer } from '../modules/rais/RaisDrawer'
import { Logo } from './Logo'

async function signOut() {
  const { logoutUrl } = await api.post<{ logoutUrl: string }>('/auth/logout').catch(() => ({ logoutUrl: '/login' }))
  window.location.assign(logoutUrl)
}

/** Main nav entries the signed-in user can open. Manage falls back to Tenants for admins without Training. */
function useNav() {
  const { user, modules } = useMe()
  return mainNav
    .map((entry) => entry.to === '/manage' && !canAccessPath(modules, '/manage', user.isAdmin) ? { ...entry, to: '/manage/tenants' } : entry)
    .filter(({ to }) => to === '/' || canAccessPath(modules, to, user.isAdmin))
}

interface NavEntry {
  to: string
  label: string
  /** Icon-only on desktop; the label becomes its accessible name and tooltip */
  icon?: LucideIcon
  visualLabel?: ComponentType
}

const mainNav: NavEntry[] = [
  { to: '/', label: 'Home' },
  { to: '/training', label: 'Training' },
  { to: '/applications', label: 'Applications' },
  { to: '/documents', label: 'Documents' },
  { to: '/surveys', label: 'Surveys' },
  { to: '/rais', label: 'RAIS', visualLabel: RaisNavLabel },
  { to: '/manage', label: 'Manage', icon: Settings },
]

function NavItem({ to, label, icon: Icon, visualLabel: VisualLabel }: NavEntry) {
  const drawer = useRaisDrawer()
  if (to === '/rais' && drawer) return (
    <button type="button" aria-label={label} aria-expanded={drawer.open} onClick={drawer.toggle}
      className={cn('relative flex h-16 items-center px-1 text-sm transition-colors', drawer.open ? 'font-semibold text-white after:absolute after:inset-x-0 after:bottom-0 after:h-[3px] after:bg-brand-500' : 'text-navy-100 hover:text-white')}>
      {VisualLabel ? <VisualLabel /> : label}
    </button>
  )
  return (
    <NavLink
      to={to}
      end={to === '/'}
      aria-label={Icon ? label : undefined}
      title={Icon ? label : undefined}
      className={({ isActive }) =>
        cn(
          'relative flex h-16 items-center px-1 text-sm transition-colors',
          isActive ? 'font-semibold text-white after:absolute after:inset-x-0 after:bottom-0 after:h-[3px] after:bg-brand-500' : 'text-navy-100 hover:text-white',
        )
      }
    >
      {Icon ? <Icon className="size-5" strokeWidth={1.75} /> : VisualLabel ? <VisualLabel /> : label}
    </NavLink>
  )
}

function UserMenu() {
  const { user: me } = useMe()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const location = useLocation()

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false)
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 rounded-full px-2 py-1 text-sm text-white hover:bg-white/5" aria-haspopup="menu" aria-expanded={open}>
        <span>{firstName(me.name)}</span>
        <ChevronDown className="size-4 text-navy-200" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-lg border border-navy-100 bg-white py-1 text-navy-900 shadow-xl">
          <div className="border-b border-navy-100 px-4 py-3">
            <p className="text-sm font-semibold">{me.name}</p>
            <p className="text-xs text-navy-500">{me.email}</p>
          </div>
          <Link role="menuitem" to="/profile" className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-navy-50">
            <CircleUser className="size-4" /> My profile
          </Link>
          <div className="my-1 border-t border-navy-100" />
          <button role="menuitem" onClick={signOut} className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-navy-600 hover:bg-navy-50">
            <LogOut className="size-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  )
}

function MobileMenu() {
  const { user: me } = useMe()
  const nav = useNav()
  const drawer = useRaisDrawer()

  return (
    <div id="mobile-menu" className="border-t border-navy-800 bg-navy-900 px-4 pb-6 pt-4 sm:px-8 lg:hidden">
      <nav aria-label="Main" className="flex flex-col">
        {nav.map(({ to, label, icon: Icon, visualLabel: VisualLabel }) => to === '/rais' && drawer ? (
          <button key={to} type="button" aria-label={label} aria-expanded={drawer.open} onClick={drawer.toggle} className="flex items-center gap-3 rounded-md border-l-[3px] border-transparent px-3 py-3 text-left text-[15px] text-navy-100 hover:bg-navy-800">
            {VisualLabel ? <VisualLabel /> : label}
          </button>
        ) : (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              cn('flex items-center gap-3 rounded-md border-l-[3px] px-3 py-3 text-[15px]', isActive ? 'border-brand-500 bg-navy-800 font-semibold text-white' : 'border-transparent text-navy-100 hover:bg-navy-800')
            }
          >
            {Icon && <Icon className="size-4.5" strokeWidth={1.75} />}
            {VisualLabel ? <VisualLabel /> : label}
          </NavLink>
        ))}
      </nav>

      <div className="mt-4 border-t border-navy-800 pt-4">
        <NavLink
          to="/profile"
          className={({ isActive }) => cn('flex items-center gap-3 rounded-md border-l-[3px] px-3 py-2', isActive ? 'border-brand-500 bg-navy-800' : 'border-transparent hover:bg-navy-800')}
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{me.name}</p>
            <p className="truncate text-xs text-navy-300">My profile · {me.email}</p>
          </div>
        </NavLink>
        <button onClick={signOut} className="mt-2 flex items-center gap-3 px-3 py-2 text-sm text-navy-300 hover:text-white">
          <LogOut className="size-4" /> Sign out
        </button>
      </div>
    </div>
  )
}

export function TopNav() {
  const [menuOpen, setMenuOpen] = useState(false)
  const { pathname, search } = useLocation()
  const nav = useNav()
  const manage = nav.find((item) => item.label === 'Manage')

  useEffect(() => {
    setMenuOpen(false)
  }, [pathname, search])

  return (
    <header className="no-print sticky top-0 z-30 bg-navy-900">
      <div className="page-container flex h-16 items-center gap-6">
        <Link to="/" aria-label="RITCCO Portal home" className="flex shrink-0 items-center">
          <Logo tagline={false} className="[&>img]:h-6" />
        </Link>
        <nav aria-label="Main" className="ml-6 hidden shrink-0 items-center gap-8 lg:flex">
          {nav.filter((item) => item.label !== 'Manage').map((n) => (
            <NavItem key={n.to} {...n} />
          ))}
        </nav>
        <div className="ml-auto flex shrink-0 items-center gap-2 lg:gap-4">
          <div className="hidden items-center gap-4 lg:flex">
            {manage && <NavItem {...manage} />}
            <span className="h-7 w-px bg-navy-700" />
            <UserMenu />
          </div>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            className="-mr-2 rounded-md p-2 text-white hover:bg-white/10 lg:hidden"
          >
            {menuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </button>
        </div>
      </div>
      {menuOpen && <MobileMenu />}
    </header>
  )
}

export function Footer({ helpLink = false }: { helpLink?: boolean }) {
  const contactEmail = useContactEmail()
  const rais = useRaisDrawer()
  return (
    <footer className="no-print border-t border-navy-100 bg-white">
      <div className="page-container grid gap-4 py-5 text-center sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:text-left">
        <p className="text-[10px] font-medium uppercase tracking-[0.06em] text-navy-700">People. Technology. Progress.</p>
        <span className="text-sm text-navy-600">© {new Date().getFullYear()} RITCCO</span>
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-navy-600 sm:justify-self-end">
          {helpLink && <a href="https://intranet.ritcco.com/kb" target="_blank" rel="noreferrer" className="hover:text-navy-900">Help</a>}
          <a href={`mailto:${contactEmail}`} className="hover:text-navy-900">Contact IT</a>
          {rais && <>
            <span className="h-6 w-px bg-navy-200" aria-hidden="true" />
            <button type="button" aria-label={rais.open ? 'Close RAIS' : 'Open RAIS'} aria-expanded={rais.open} onClick={rais.toggle} className="flex size-9 items-center justify-center rounded-md text-navy-700 transition hover:bg-navy-50 hover:text-navy-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500">
              {rais.open ? <X className="size-5" /> : <MessageCircle className="size-5" />}
            </button>
          </>}
        </div>
      </div>
    </footer>
  )
}

/** Thin bar at the very top while the next page's data loads; the current page stays put until then. */
function NavigationProgress() {
  const navigation = useNavigation()
  const loading = navigation.state !== 'idle'
  return (
    <div aria-hidden className="pointer-events-none fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden">
      <div className={cn('h-full bg-brand-500 transition-[width,opacity] ease-out', loading ? 'w-4/5 opacity-100 duration-[2500ms]' : 'w-full opacity-0 duration-300')} />
    </div>
  )
}

export function AppShell() {
  const { pathname } = useLocation()
  const { user, modules } = useMe()
  const allowed = canAccessPath(modules, pathname, user.isAdmin)
  return (
    <RaisDrawerProvider>
    <div className="flex min-h-screen flex-col">
      <ScrollRestoration />
      <NavigationProgress />
      <TopNav />
      <main className="flex-1">
        {/* Loaders prefetch every page's data, so this boundary should never show; it guards against a blank app. */}
        {allowed ? <Suspense fallback={null}><Outlet /></Suspense> : <Page className="py-20">
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-navy-500">Restricted</p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight">Access not available</h1>
          <p className="mt-3 max-w-xl text-lg text-navy-500">Your tenant does not have access to this module.</p>
          <Link to="/" className="mt-6 inline-flex text-sm font-medium text-brand-600 hover:underline">Back to Home →</Link>
        </Page>}
      </main>
      <Footer helpLink={canUse(modules, 'knowledge-base')} />
    </div>
    </RaisDrawerProvider>
  )
}

/**
 * First-load placeholder: the shell's header at the same size and place, and an empty page area that keeps the
 * footer out of view, so when the real page renders nothing already on screen moves.
 */
export function ShellSkeleton() {
  return (
    <div aria-busy="true">
      <header className="sticky top-0 z-30 bg-navy-900">
        <div className="page-container flex h-16 items-center">
          <Logo tagline={false} className="[&>img]:h-6" />
        </div>
      </header>
      <main className="min-h-[calc(100vh-4rem)]" />
    </div>
  )
}

export function Page({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('page-container py-10', className)}>{children}</div>
}
